import { useQuery } from '@tanstack/react-query';

import { trackerScopeQueryOptions, type TrackerScope } from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';

/**
 * The rows endpoint's `scope` parameter for a stored scope. The board alone,
 * including a selection of nothing but its own workspace or a scope that could
 * not be read, is sent as `board` rather than left out: the server reads a
 * missing scope as the stored one, which may be wider than what this board
 * shows. `board` costs the server no platform calls. A selection is sent as
 * stored: the server intersects it with what the person can read.
 */
export function rowsScopeParam(scope: TrackerScope | undefined, boardWorkspaceId: string): string {
  if (scope?.mode === 'all') return scope.mode;
  if (scope?.mode === 'selected' && scope.workspaceIds.some((id) => id !== boardWorkspaceId)) {
    return scope.workspaceIds.join(',');
  }
  // eslint-disable-next-line i18next/no-literal-string -- an API scope value
  return 'board';
}

export interface WorkspaceScope {
  workspaceId: string;
  scope: TrackerScope | undefined;
  rowsScope: string;
  /**
   * The scope has an answer, or failed to get one and the board falls back to
   * its own workspace. Until then the rows query waits, so it does not fetch
   * once for the board and again for the stored scope.
   */
  settled: boolean;
}

export function useWorkspaceScope(trackerId: string): WorkspaceScope {
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const query = useQuery(trackerScopeQueryOptions(workspaceId, trackerId));
  return {
    workspaceId,
    scope: query.data,
    rowsScope: rowsScopeParam(query.data, workspaceId),
    settled: !query.isPending,
  };
}
